<template>
    <div class="max-w-4xl mx-auto p-6 text-gray-800 overflow-auto">
      <h1 class="text-3xl font-bold mb-2">Terms of Service</h1>
      <p class="text-sm text-gray-500 mb-6">Last Updated: April 19, 2025</p>

      <div v-for="(section, index) in termsSections" :key="index" class="mb-6">
        <h2 class="text-xl font-semibold mb-2">{{ section.title }}</h2>
        <div v-html="section.content" class="space-y-2 text-gray-700"></div>
      </div>
    </div>
  </template>

<script setup>
const termsSections = [
  {
    title: '1. Acceptance of Terms',
    content: `
        <p>By accessing or using PapoteAI (the “Service”), you agree to be bound by these 
            Terms of Service (“Terms”) and our Privacy Policy. If you do not agree with all 
            of these Terms, do not use the Service. Use of the Service is also subject to 
            any additional terms provided within specific features or sections of the app.</p>
      `
  },
  {
    title: '2. Description of the Service',
    content: `
        <p>The Service is designed to help users learn and practice new languages through 
            interactive role-playing conversations with an AI agent. The AI provides 
            feedback on users' messages and offers language tips to enhance their learning. 
            The Service may incorporate various multimedia and interactive features.</p>
      `
  },
  {
    title: '3. User Accounts and Registration',
    content: `
        <ul class="list-disc pl-5 space-y-1">
          <li><strong>Eligibility:</strong> You must be at least 13 years old (or the applicable minimum age in your jurisdiction) to use the Service. By registering, you represent and warrant that you meet this requirement.</li>
          <li><strong>Account Creation:</strong> To access certain features, you may be required to create an account. You agree to provide accurate, current, and complete information during the registration process and update such information as necessary.</li>
          <li><strong>Security:</strong> You are responsible for maintaining the confidentiality of your account credentials. You must immediately notify us of any unauthorized use or breach of security.</li>
          <li><strong>Termination:</strong> We reserve the right to suspend or terminate your account at our sole discretion for any violation of these Terms or if your behavior is deemed harmful to other users or the integrity of the Service.</li>
        </ul>
      `
  },
  {
    title: '4. Use of the Service',
    content: `
        <ul class="list-disc pl-5 space-y-1">
          <li><strong>License:</strong> Subject to your compliance with these Terms, PapoteAI grants you a limited, non-exclusive, non-transferable, and revocable license to access and use the Service for personal, non-commercial purposes.</li>
          <li><strong>User Responsibilities:</strong> You agree to use the Service only for lawful purposes and in accordance with these Terms. You will not attempt to impair the functionality or security of the Service or engage in any conduct that may negatively affect other users.</li>
          <li><strong>Prohibited Activities:</strong> You agree not to:
            <ul>
                <li>Upload, post, or transmit any content that is unlawful, harmful, defamatory, or violates any third-party rights.</li>
                <li>Use the Service to harass, abuse, or harm another individual.</li>
                <li>Reverse-engineer, decompile, or attempt to extract the source code from the AI or any underlying systems unless explicitly permitted.</li>
            </ul>
          </li>
        </ul>
      `
  },
  {
    title: '5. AI-Driven Role-Playing and Feedback',
    content: `
        <p>The AI's responses are educational and algorithmically generated. PapoteAI does not guarantee 100% accuracy or appropriateness in every context.</p>
        <p>Always use your judgment when applying AI-generated suggestions.</p>
      `
  },
  {
    title: '6. User-Generated Content',
    content: `
        <ul class="list-disc pl-5 space-y-1">
            <li><strong>Ownership:</strong> You retain ownership of the content you submit, but grant PapoteAI a worldwide, royalty-free license to use, reproduce, and analyze it to provide and improve the Service.</li>
            <li><strong>Responsibility:</strong> You are responsible for ensuring your content complies with applicable laws and does not infringe on any third-party rights.</li>
            <li><strong>Monitoring:</strong> We may review user content for compliance but are not obligated to do so.</li>
        </ul>
      `
  },
  {
    title: '7. Intellectual Property Rights',
    content: `
      <ul class="list-disc pl-5 space-y-1">
        <li><strong>Our Content:</strong> The Service, including all underlying software, design elements, AI algorithms, and other proprietary materials, is protected by intellectual property laws. No rights are transferred to you except for the limited license granted herein.</li>
        <li><strong>User Content:</strong> You retain the intellectual property rights in your own user content. However, by using the Service, you grant us permission to use your content in connection with the provision of the Service as specified in Section 6.</li>
      </ul>
    `
  },
  {
    title: '8. Privacy and Data Use',
    content: `
      <ul class="list-disc pl-5 space-y-1">
        <li><strong>Privacy Policy:</strong> Your use of the Service is also governed by our Privacy Policy, which outlines how we collect, store, and use your personal information. Please review the Privacy Policy carefully.</li>
        <li><strong>Data Collection:</strong> In order to improve our AI and enhance the user experience, we may collect and analyze your interaction data. By using the Service, you consent to this data collection and acknowledge that it may be aggregated or anonymized for research and development purposes.</li>
      </ul>
    `
  },
  {
    title: '9. Disclaimers and Limitation of Liability',
    content: `
      <ul class="list-disc pl-5 space-y-1">
        <li><strong>No Warranty:</strong> The Service is provided “as is” and “as available” without warranties of any kind, either express or implied, including warranties of accuracy, fitness for a particular purpose, or non-infringement.</li>
        <li><strong>Limitation of Liability:</strong> Under no circumstances shall PapoteAI, its affiliates, or its suppliers be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of the Service. In no event will our total liability exceed the amount you paid (if any) to use the Service.</li>
        <li><strong>Feedback Accuracy:</strong> While the AI aims to provide accurate feedback and language tips, we make no guarantee as to the correctness or appropriateness of the advice. The use of any such advice is at your sole risk.</li>
      </ul>
    `
  },
  {
    title: '10. Indemnification',
    content: `
      <p>You agree to indemnify, defend, and hold harmless PapoteAI and its officers, directors, employees, and agents from and against any claims, obligations, losses, liabilities, damages, and expenses (including attorney fees) arising out of or related to your use of the Service, violation of these Terms, or infringement of any rights of a third party.</p>
    `
  },
  {
    title: '11. Termination',
    content: `
      <ul class="list-disc pl-5 space-y-1">
        <li><strong>Termination by You:</strong> You may terminate your account at any time by following the instructions provided within the app.</li>
        <li><strong>Termination by Us:</strong> We reserve the right to terminate or suspend your access to the Service at any time, without prior notice, for any reason including but not limited to violations of these Terms.</li>
        <li><strong>Surviving Provisions:</strong> Upon termination, any rights and licenses granted to you under these Terms will immediately cease, and you agree to delete any copies of the Service’s proprietary information in your possession.</li>
      </ul>
    `
  },
  {
    title: '12. Governing Law and Dispute Resolution',
    content: `
      <ul class="list-disc pl-5 space-y-1">
        <li><strong>Governing Law:</strong> These Terms shall be governed by and construed in accordance with the laws of France, without regard to its conflict of laws principles.</li>
        <li><strong>Dispute Resolution:</strong> Any disputes arising from these Terms or your use of the Service shall be resolved first through informal negotiations. If a resolution cannot be reached, you agree to submit to binding arbitration in France, except that you may seek injunctive or other equitable relief in any court of competent jurisdiction.</li>
        <li><strong>Class Action Waiver:</strong> To the fullest extent permitted by applicable law, you agree that any dispute shall be resolved only on an individual basis and not as part of a class or consolidated action.</li>
      </ul>
    `
  },
  {
    title: '13. Modifications to the Terms',
    content: `
      <p>We reserve the right to modify these Terms at any time. If significant changes are made, we will provide notice through the Service and update the “Last Updated” date at the top of these Terms. Your continued use of the Service after such modifications constitutes acceptance of the revised Terms.</p>
    `
  },
  {
    title: '14. General Provisions',
    content: `
      <ul class="list-disc pl-5 space-y-1">
        <li><strong>Entire Agreement:</strong> These Terms constitute the entire agreement between you and PapoteAI regarding the use of the Service, superseding any prior agreements.</li>
        <li><strong>Severability:</strong> If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions will continue to be valid and enforceable.</li>
        <li><strong>Waiver:</strong> No waiver of any term of these Terms shall be deemed a further or continuing waiver of such term or any other term.</li>
      </ul>
    `
  }
]
</script>

  <style scoped>
  /* Optional: Additional styling if needed */
  </style>
